import Avatar from "./avatar";
import Date from "./date";
import CoverImage from "./cover-image";
import PostTitle from "./post-title";
import { useState, useRef, useEffect } from "react";

export default function PostHeader({ title, coverImage, date, author, readingTime, url }) {
  const [shareOpen, setShareOpen] = useState(false);
  const shareRef = useRef(null);

  useEffect(() => {
    if (!shareOpen) return;
    function handleClickOutside(event) {
      if (shareRef.current && !shareRef.current.contains(event.target)) {
        setShareOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [shareOpen]);

  // Scroll to ReviewSection handler
  const handleCommentClick = () => {
    const reviewSection = document.getElementById("review-section");
    if (reviewSection) {
      reviewSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="w-full bg-[#F9FBFF] px-4 md:px-10 pt-8 pb-8 md:pb-12">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center lg:items-start gap-6 lg:gap-16 relative">
        <div className="flex-1 flex justify-center lg:justify-end">
          <div className="rounded-2xl overflow-hidden shadow-sm max-w-full">
            <CoverImage title={title} url={coverImage} />
          </div>
        </div>

        <div className="flex-1 w-full text-center lg:text-center">
          <div className="text-3xl md:text-3xl font-bold text-black leading-snug mb-4">
            <PostTitle>{title}</PostTitle>
          </div>
          <p className="text-sm text-gray-500 mb-2">
            <Date dateString={date} />
          </p>
          <div className="flex items-center justify-center gap-4 mt-2">
            {/* {author?.profile_image && (
              <img
                src={author.profile_image}
                alt={author.name}
                className="w-10 h-10 rounded-full shadow-md"
              />
            )} */}
            <div className="flex items-center space-x-1">
              <div className="w-3 h-0.5 bg-gray-700"></div>
              <p className="text-sm text-gray-700 font-bold">
                {typeof author === 'string' ? author : author?.name}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Place the absolute div here, inside the main container, so it overlays correctly */}
      <div className="max-w-7xl mx-auto relative mt-12 lg:mt-0" style={{ minHeight: "0" }}>
        <div className="absolute bottom-0 right-0 flex items-center space-x-3">
          <p className="text-sm text-gray-700 font-bold px-1">{readingTime}</p>
          {/* Comment button */}
          <button
            className="flex items-center px-1 py-1 rounded hover:bg-gray-200 transition"
            title="Comments"
            onClick={handleCommentClick}
          >
            <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span className="ml-1 text-sm text-gray-700">Comment</span>
          </button>
          {/* Share button */}
          <div className="relative" ref={shareRef}>
            <button
              className="flex items-center px-1 py-1 rounded hover:bg-gray-200 transition"
              title="Share"
              onClick={() => setShareOpen((prev) => !prev)}
            >
              <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" />
              </svg>
              <span className="ml-1 text-sm text-gray-700">Share</span>
            </button>
            <div className={`${shareOpen ? "flex" : "hidden"} flex-col absolute right-0 mt-2 bg-white border border-gray-200 rounded shadow-lg z-10 min-w-[160px]`}>
              {(() => {
              

                return (
                  <>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(title + " " + url)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 hover:bg-gray-100 flex items-center text-sm"
                    >
                      <img src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/whatsapp.svg" alt="WhatsApp" className="w-4 h-4 mr-2" />
                      WhatsApp
                    </a>
                    <a
                      href={`mailto:?subject=${title}&body=${encodeURIComponent(url)}`}
                      className="px-4 py-2 hover:bg-gray-100 flex items-center text-sm"
                    >
                      <img src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/maildotru.svg" alt="Email" className="w-4 h-4 mr-2" />
                      Email
                    </a>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 hover:bg-gray-100 flex items-center text-sm"
                    >
                      <img src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/facebook.svg" alt="Facebook" className="w-4 h-4 mr-2" />
                      Facebook
                    </a>
                    <a
                      href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${title}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 hover:bg-gray-100 flex items-center text-sm"
                    >
                      <img src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/x.svg" alt="Twitter" className="w-4 h-4 mr-2" />
                      Twitter/X
                    </a>
                    <a
                      href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${title}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 hover:bg-gray-100 flex items-center text-sm"
                    >
                      <img src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/linkedin.svg" alt="LinkedIn" className="w-4 h-4 mr-2" />
                      LinkedIn
                    </a>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
